/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The share crop of a page's own image: what og:image, og:image:secure_url and
 * twitter:image point at when a page shares an image of its own (an article's
 * hero, an SEO-tab image) instead of the default /og-image.jpg.
 *
 * Why a crop of its own (2026-10-05): the pages' images are WebP, and LinkedIn
 * skips a WebP share image; they come in any shape (the article heroes are
 * 1024×1024), so every platform cut them its own way, and no head could say how
 * big they were. The share crop is always a 1200×630 JPEG, cut from the centre
 * unless positioned (below) — the size and type the default image has — so the
 * head states its type and dimensions like it does for the default. It is made
 * on request by the image
 * worker (server/routes/imageVariants.ts → scripts/imageVariantWorker.mjs) and
 * cached beside the responsive variants; 64–108 KB for the current heroes,
 * well under WhatsApp's ~300 KB ceiling.
 *
 * URL: `/share/<images|uploads>/<path of the original>.jpg`. The original's own
 * extension stays in the path (`/share/images/hero.webp.jpg`), so the route
 * resolves exactly one file, and every scraper and CDN sees a .jpg.
 *
 * Positioned (2026-10-10): an article hero that an editor positioned
 * (`heroImageFocus`, the one "<x>% <y>%" format of src/lib/imageFocus.ts) is
 * cut where the page's hero frame shows it — the crop is that position's
 * `object-position` in a 1200×630 frame — instead of from the centre, which
 * kept sky and lost the subject. The position rides in the query,
 * `?focus=30%25%2061%25`, so a moved position is a NEW address: no CDN, browser
 * or scraper cache can hand out the old cut under it. No position — or dead
 * centre, which is the same value — adds nothing, so every unpositioned
 * page's address, and its baked head, is byte for byte what it was.
 *
 * Plain ESM JavaScript: the bake (scripts/prerender.mjs), the server and the
 * client import it alike.
 */

import { SEO_HOST } from './articleSeo.js';
import { isValidFocus, normalizeFocus } from './imageFocusCore.js';

export const SHARE_IMAGE_PREFIX = '/share';
export const SHARE_IMAGE_WIDTH = 1200;
export const SHARE_IMAGE_HEIGHT = 630;
export const SHARE_IMAGE_TYPE = 'image/jpeg';
/** The query parameter that carries a crop's position, as stored ("30% 61%"), URI-encoded. */
export const SHARE_IMAGE_FOCUS_PARAM = 'focus';

/** The raster formats the image worker reads — the responsive variants' list. */
const SOURCE_EXT_RE = /\.(?:webp|png|jpe?g)$/i;

/**
 * The position a crop is cut at: the canonical stored form, or '' for the
 * centre — no position, dead centre and anything that is not a position.
 *
 * @param {unknown} focus
 * @returns {string}
 */
export function shareImageFocusOf(focus) {
  const canonical = normalizeFocus(focus);
  return canonical && isValidFocus(canonical) ? canonical : '';
}

/**
 * The share crop's site-relative address for an image the site serves itself,
 * or null for anything the route cannot crop: another host, an SVG or GIF, a
 * URL with a query or fragment, a path outside /images and /uploads or one
 * that climbs out of its folder.
 *
 * @param {unknown} image  As stored: site-relative (`/images/x.webp`) or absolute on `host`.
 * @param {string} [host]
 * @param {unknown} [focus]  The image's position ("<x>% <y>%"); '' or absent = centre.
 * @returns {string | null}
 */
export function shareImagePathOf(image, host = SEO_HOST, focus = '') {
  if (typeof image !== 'string') return null;
  let p = image.trim();
  if (p.startsWith(`${host}/`)) p = p.slice(host.length);
  const m = /^\/(images|uploads)\/(.+)$/.exec(p);
  if (!m) return null;
  const rel = m[2];
  if (!SOURCE_EXT_RE.test(rel) || /[?#\\\0]/.test(rel)) return null;
  if (rel.split('/').some((seg) => seg === '' || seg === '.' || seg === '..')) return null;
  const position = shareImageFocusOf(focus);
  const query = position ? `?${SHARE_IMAGE_FOCUS_PARAM}=${encodeURIComponent(position)}` : '';
  return `${SHARE_IMAGE_PREFIX}/${m[1]}/${rel}.jpg${query}`;
}
